import { Link, useLocation, useNavigate } from 'react-router-dom';
import { pathForFlow } from '../../auth';
import { Flows, AuthenticatorType } from '../../lib/allauth';
import { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Typography, Box } from '@mui/material';
import BackgroundContainer from '../../components/BackgroundContainer';
import BaseFormContainer from '../../components/BaseFormContainer';
import BaseHeader from '../../components/BaseHeader';

const Subtitle = styled(Typography)({
  fontFamily: 'Playfair Display, serif',
  fontSize: '1.5rem',
  color: '#ffbfe1',
  textAlign: 'center',
  textShadow: '0px 2px 6px rgba(0, 0, 0, 0.5)',
  lineHeight: '1.4',
});

const AlternativeLink = styled(Link)({
  fontFamily: 'Playfair Display, serif',
  color: '#ffbfe1',
  fontSize: '1rem',
  textDecoration: 'underline',
  display: 'block',
  textAlign: 'center',
  marginTop: '8px',
});

const flowLabels = {};
flowLabels[Flows.REAUTHENTICATE] = 'Use your password';
flowLabels[`${Flows.MFA_REAUTHENTICATE}:${AuthenticatorType.TOTP}`] = 'Use your authenticator app';
flowLabels[`${Flows.MFA_REAUTHENTICATE}:${AuthenticatorType.RECOVERY_CODES}`] = 'Use a recovery code';
flowLabels[`${Flows.MFA_REAUTHENTICATE}:${AuthenticatorType.WEBAUTHN}`] = 'Use security key';

function flowsToMethods(flows) {
  const methods = [];
  flows.forEach(flow => {
    if (flow.id === Flows.MFA_REAUTHENTICATE) {
      flow.types.forEach(typ => {
        const id = `${flow.id}:${typ}`;
        methods.push({ label: flowLabels[id], id, path: pathForFlow(flow, typ) });
      });
    } else {
      methods.push({ label: flowLabels[flow.id] || flow.id, id: flow.id, path: pathForFlow(flow) });
    }
  });
  return methods;
}

export default function ReauthenticateFlow(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const flows = location.state?.reauth?.data?.flows;

  useEffect(() => {
    if (!flows) {
      navigate('/account/profile', { replace: true });
    }
  }, [flows, navigate]);

  if (!flows) return null;

  const methods = flowsToMethods(flows);
  const alternatives = methods.filter(method => method.id !== props.flow);

  return (
    <BackgroundContainer>
      <BaseFormContainer>
        <BaseHeader sx={{ fontSize: '2rem' }}>Confirm Access</BaseHeader>
        <Subtitle sx={{
          marginBottom: '20px',
          fontSize: '1.2rem',
          '@media (max-width: 400px)': { fontSize: '1.1rem' },
          '@media (max-width: 360px)': { fontSize: '1rem' },
          '@media (max-width: 320px)': { fontSize: '0.95rem' },
        }}>
          Please reauthenticate to continue.
        </Subtitle>

        {props.children}

        {alternatives.length > 0 && (
          <Box sx={{ marginTop: '16px' }}>
            <Subtitle sx={{ fontSize: '1rem', marginBottom: '4px', color: 'rgba(255,191,225,0.6)' }}>
              Other options
            </Subtitle>
            {alternatives.map(method => (
              <AlternativeLink
                key={method.id}
                replace
                state={location.state}
                to={method.path + location.search}
              >
                {method.label}
              </AlternativeLink>
            ))}
          </Box>
        )}
      </BaseFormContainer>
    </BackgroundContainer>
  );
}
